/**
 * ConflictToast — Plan 04-03 / UI-SPEC §"<ConflictToast />".
 *
 * Informational toast that surfaces D-10 last-writer-wins reconciliations:
 * text edits whose local diff was non-trivial (>3 chars) and delete-vs-edit
 * collisions.
 *
 * Placement (issue #59) is identical to ToastViewport's POSITION_CLASSES —
 * rationale lives there; keep the two in sync. In short: `bottom-28` clears the
 * nav pill and the assistant button (#153), `md:` is the rail breakpoint, `z-40` is the #45 ladder's toast rung.
 *
 * Reads from `useShoppingStore.lastConflict`; auto-dismisses after 4.5s by
 * calling `clearConflict()`. NEVER `role="alert"` (T-04-toastTone): this is
 * informational, not an error — uses `role="status" aria-live="polite"`.
 *
 * D-10: text-edit benign reconciliations stay silent — the mutation handler
 * is responsible for filtering before pushing into the store.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShoppingStore } from './shoppingStore';

const AUTO_DISMISS_MS = 4_500;

export function ConflictToast() {
  const { t } = useTranslation();
  const lastConflict = useShoppingStore((s) => s.lastConflict);
  const clearConflict = useShoppingStore((s) => s.clearConflict);

  useEffect(() => {
    if (!lastConflict) return undefined;
    const handle = window.setTimeout(() => clearConflict(), AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [lastConflict, clearConflict]);

  if (!lastConflict) return null;

  const message =
    lastConflict.kind === 'edit'
      ? t('shopping.conflict.edit', { nick: lastConflict.nick })
      : t('shopping.conflict.removed', { nick: lastConflict.nick });

  return (
    <div
      data-testid="shopping-conflict-toast"
      role="status"
      aria-live="polite"
      className="fixed bottom-28 left-4 right-4 z-40 mx-auto max-w-sm rounded-lg border bg-shop-conflict-bg text-shop-conflict-fg shadow-md p-4 text-sm md:left-auto md:right-4 md:max-w-sm"
      style={{
        borderColor: 'var(--color-shop-conflict-border)',
      }}
    >
      {message}
    </div>
  );
}
